import { numFont } from '../../../utils/fonts';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Avatar, Button, Stack } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTimeclockStore } from '../../../storeTimeclock';
import { getOrganizationId } from '../../../store';
import { TOKENS, Plate, LiveDot, hms, useNow, elapsedSeconds, livePresenceSeconds, todayStr } from './worktimeLib';

const STATE_ORDER = ['warn', 'clocking', 'break', 'day', 'off'];

const initials = (name) =>
  (name || '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/**
 * Manager live board ("Kuka on töissä nyt"), foreman+. Consumes the existing /board (running
 * sessions org-wide) on the established poll; timers tick from server started_at. "Korjaa" routes
 * to the reconcile editor for that employee's flagged day (the on-behalf correction path).
 */
export default function ManagerBoardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const board = useTimeclockStore((s) => s.board);
  const fetchBoard = useTimeclockStore((s) => s.fetchBoard);
  const nowMs = useNow();
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchBoard();
    const id = setInterval(fetchBoard, 20000);
    const orgId = getOrganizationId();
    if (orgId) {
      axios
        .get(`/api/employees/?organization=${orgId}`)
        .then((res) => setEmployees((Array.isArray(res.data) ? res.data : res.data?.results || []).filter((e) => e.state === 'active')))
        .catch(() => setEmployees([]));
    }
    return () => clearInterval(id);
  }, [fetchBoard]);

  const byEmp = useMemo(() => {
    const m = {};
    for (const s of board) {
      const e = (m[s.employee] = m[s.employee] || { name: s.employee_name, day: null, wo: null, brk: null, review: false });
      if (s.kind === 'day') e.day = s;
      if (s.kind === 'work_order') e.wo = s;
      if (s.kind === 'break') e.brk = s;
      if (s.needs_review) e.review = true;
    }
    return m;
  }, [board]);

  const rows = useMemo(() => {
    // Show every active employee; those not in the board are "off".
    const base = employees.length ? employees : Object.keys(byEmp).map((id) => ({ id: Number(id), name: byEmp[id].name }));
    return base
      .map((emp) => {
        const e = byEmp[emp.id];
        const state = e?.review ? 'warn' : e?.wo ? 'clocking' : e?.brk ? 'break' : e?.day ? 'day' : 'off';
        return { id: emp.id, name: emp.name, state, day: e?.day, wo: e?.wo, brk: e?.brk };
      })
      .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
  }, [employees, byEmp]);

  const counts = useMemo(
    () => ({
      active: rows.filter((r) => r.state === 'clocking').length,
      idle: rows.filter((r) => r.state === 'day').length,
      review: rows.filter((r) => r.state === 'warn').length,
    }),
    [rows],
  );

  const stateChip = (r) => {
    if (r.state === 'warn')
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: TOKENS.red, fontSize: 13, fontWeight: 600 }}>
          <WarningAmberIcon sx={{ fontSize: 16 }} /> {t('worktime.board.tooLong')}
        </Box>
      );
    if (r.state === 'clocking')
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: TOKENS.greenDark, fontSize: 13 }}>
          <LiveDot size={7} /> {t('worktime.board.clocking')}
        </Box>
      );
    if (r.state === 'break')
      return <Box sx={{ color: TOKENS.amber, fontSize: 13 }}>● {t('worktime.board.onBreak')}</Box>;
    if (r.state === 'day')
      return <Box sx={{ color: TOKENS.amber, fontSize: 13 }}>● {t('worktime.board.dayOnly')}</Box>;
    return <Box sx={{ color: TOKENS.subtle, fontSize: 13 }}>○ {t('worktime.board.off')}</Box>;
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupsIcon sx={{ color: TOKENS.accent }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('worktime.board.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: TOKENS.muted, fontSize: 13 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <LiveDot size={7} /> {t('worktime.board.activeCount', { count: counts.active })}
          </Box>
          <span>·</span>
          <span>{t('worktime.board.idleCount', { count: counts.idle })}</span>
          {counts.review > 0 && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: TOKENS.red }}>
              <WarningAmberIcon sx={{ fontSize: 15 }} /> {t('worktime.board.reviewCount', { count: counts.review })}
            </Box>
          )}
        </Box>
      </Box>

      <Stack spacing={1}>
        {rows.map((r) => (
          <Box
            key={r.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${TOKENS.border}`,
              backgroundColor: r.state === 'warn' ? TOKENS.redSoft : TOKENS.surface,
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: r.state === 'off' ? TOKENS.subtle : TOKENS.navy, fontSize: 13 }}>
              {initials(r.name)}
            </Avatar>
            <Box sx={{ width: 160, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                {r.name}
              </Typography>
              {stateChip(r)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {r.wo ? (
                <>
                  <Plate size={13}>{r.wo.maintenance_info?.vehicle_registration || '—'}</Plate>
                  <Typography sx={{ fontSize: 12, color: TOKENS.muted }} noWrap>
                    {r.wo.maintenance_info?.description || ''}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 13, color: TOKENS.subtle }}>{t('worktime.board.noActiveWork')}</Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right', minWidth: 90 }}>
              <Typography sx={{ ...numFont, fontWeight: 700, color: r.state === 'warn' ? TOKENS.red : TOKENS.fg }}>
                {r.wo ? hms(elapsedSeconds(r.wo.started_at, nowMs)) : '—'}
              </Typography>
              <Typography sx={{ ...numFont, fontSize: 12, color: TOKENS.muted }}>
                {r.day ? hms(livePresenceSeconds(r.day.day_prior_seconds, r.day.started_at, nowMs)) : '—'}
              </Typography>
            </Box>
            {r.state === 'warn' && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/time-clock/confirm?employee=${r.id}&date=${todayStr()}`)}
                sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}
              >
                {t('worktime.board.fix')}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
